import { TextField } from '../primitives/index.js'
import cn from 'classnames'
import type { ChangeEvent, FC, HTMLAttributes, KeyboardEvent } from 'react'
import { useState } from 'react'
import { Tag } from './tag.js'
import styles from './tag-list.module.css'

export interface TagListProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  className?: string
  tags?: string[]
  fullWidth?: boolean
  placeholder?: string
  variant?: 'outlined' | 'filled'
  size?: 'small' | 'medium'
  // If onChange is provided, the input field will be displayed
  onChange?(tags: string[]): void
}

export const TagList: FC<TagListProps> = props => {
  const {
    className,
    tags: defaultTags = [],
    size = 'medium',
    onChange,
    variant = 'outlined',
    fullWidth = true,
    placeholder = 'Add tag...',
    ...restProps
  } = props
  const [name, setName] = useState('')

  const tags = defaultTags ?? []

  const handleDelete = (tag: string) => () => onChange && onChange(tags.filter(el => el !== tag))
  const handleChange = (ev: ChangeEvent<HTMLInputElement>) => setName(ev.target.value)
  const handleReturn = (ev: KeyboardEvent<HTMLInputElement>) => {
    const val: string = ev.currentTarget.value
    if (ev.key === 'Enter' && val) {
      setName('')
      if (tags.includes(val)) {
        return
      }
      if (onChange) {
        onChange([...tags, val.toLowerCase()])
      }
    }
  }

  return (
    <div className={cn(styles.root, className)} {...restProps}>
      <div className={styles.tags}>
        {tags.map(c => (
          <Tag key={c} label={c} size={size} variant={variant} onDelete={onChange ? handleDelete(c) : undefined} />
        ))}
      </div>
      {onChange && (
        <TextField
          id="tag-list"
          className={styles.input}
          fullWidth={fullWidth}
          value={name}
          placeholder={placeholder}
          onKeyUp={handleReturn}
          onChange={handleChange}
          autoComplete="off"
          tabIndex={restProps.tabIndex}
        />
      )}
    </div>
  )
}

export default TagList
